<script setup lang="ts">
import { Field as VeeField } from 'vee-validate'

import { useBasketStore } from '@/store/basket/basket'
import { useCityStore } from '@/store/city/city'
import { useUserStore } from '@/store/user/user'

import { DeliveryMethods } from '@/api/modules/basket/types'
import { BasketPurchaseButtonEmit, BasketStep } from '@/types/basket'

import BasketPricePanelRow from '@/components/Basket/basket-price-panel/BasketPricePanelRow.vue'
import BasketPriceShortPanel from '@/components/Basket/basket-price-panel/BasketPriceShortPanel.vue'
import BasketTitle from '@/components/Basket/basket-utils/BasketTitle.vue'

type Props = {
  basketStep: BasketStep
  isGiftNeed?: boolean
  isLoading: boolean
  isPriceSectionSticky: boolean
  amountToPayWithSymbol: string
}

const cityStore = useCityStore()
const basketStore = useBasketStore()
const userStore = useUserStore()

const emit = defineEmits([
  'orderCheckout',
  ...Object.keys(BasketPurchaseButtonEmit),
])
const props = defineProps<Props>()

const isAuthenticated = computed(() => userStore.isAuthenticated)

const basket = computed(() => basketStore.data)

const currencySymbol = computed(() => cityStore.currencySymbol)

const selectedAddress = computed(() => basketStore.selectedAddress)

const isBasketOnFirstStep = computed(
  () => props?.basketStep === BasketStep.FIRST,
)

const isDeliveryMethod = computed(
  () => basket.value?.delivery.method === DeliveryMethods.COURIER,
)

const isDeliveryMethodTitle = computed(
  () => basketStore.basketSelectedDeliveryMethod,
)

const isDeliveryMethodValue = computed(() => {
  if (!selectedAddress.value) return ''

  if (!basket.value.delivery.cost?.value) {
    return 'Бесплатно'
  }

  return `${basket.value.delivery.cost?.value} ${currencySymbol.value}`
})

const basketSelectedPaymentMethod = computed(
  () => basketStore.basketSelectedPaymentMethod,
)

const showChangeInfo = computed(
  () =>
    basket.value.change !== 0 &&
    basket.value.change > basket.value.amountToPay &&
    !!basketSelectedPaymentMethod.value &&
    basketSelectedPaymentMethod.value === 'Наличные',
)

const toAccrueAmountBonuses = computed(
  () => basket?.value?.bonuses?.toAccrueAmount || 0,
)

const scrollToErrorBlock = (errorBlock: string, error: boolean) => {
  if (error) return

  document
    .querySelector<HTMLElement>(`#${errorBlock}`)
    ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

const orderCheckout = () => emit('orderCheckout')

const goToAuthorize = () => emit('goToAuthorize')

const goToSecondStep = () => emit('goToSecondStep')
</script>

<template>
  <vee-field id="price-panel" vid="detail" name="detail" as="section">
    <basket-title
      v-if="!isPriceSectionSticky"
      class="price-panel-title"
      title="Стоимость заказа"
    />

    <section v-if="!isPriceSectionSticky" class="container">
      <basket-price-panel-row
        v-if="isAuthenticated"
        :title="isDeliveryMethodTitle"
        :value="selectedAddress || 'Не указан'"
      />

      <basket-price-panel-row
        v-if="isDeliveryMethod && isAuthenticated"
        title="Доставка"
      >
        <div>{{ isDeliveryMethodValue }}</div>
      </basket-price-panel-row>

      <basket-price-panel-row
        v-if="isAuthenticated && !isBasketOnFirstStep"
        title="Оплата"
        :value="basketSelectedPaymentMethod"
        @click="
          scrollToErrorBlock('basket-payment', !basketSelectedPaymentMethod)
        "
      />

      <basket-price-panel-row
        v-if="showChangeInfo && isAuthenticated && !isBasketOnFirstStep"
        title="Сдача с"
        :value="`${basket.change} ${currencySymbol}`"
        @click="scrollToErrorBlock('change', showChangeInfo)"
      />

      <basket-price-panel-row
        title="Сумма заказа"
        :value="`${basket.totalAmount} ${currencySymbol}`"
      />

      <basket-price-panel-row
        v-if="isAuthenticated"
        title="Начислено бонусов"
        @click="scrollToErrorBlock('bonuses', false)"
      >
        <p v-if="isAuthenticated">
          {{ toAccrueAmountBonuses }}
        </p>
        <p v-else>Нужно авторизоваться</p>
      </basket-price-panel-row>

      <basket-price-panel-row
        v-if="basket.bonuses.toSpendAmount && isAuthenticated"
        title="Скидка бонусами"
        :value="`${basket.bonuses.toSpendAmount} ${currencySymbol}`"
      />

      <basket-price-panel-row
        v-if="basket.promocode.discountAmount && isAuthenticated"
        title="Скидка по промокоду"
        :value="`${basket.promocode.discountAmount} ${currencySymbol}`"
      />

      <hr class="container__line" />

      <basket-price-panel-row
        title="Итого"
        :value="amountToPayWithSymbol"
        is-title
      />
    </section>

    <basket-price-short-panel
      :cost="basket.delivery.cost"
      :amount-to-pay="amountToPayWithSymbol"
      :is-authenticated="isAuthenticated"
      :is-gift-need="isGiftNeed"
      :is-loading="isLoading"
      :basket-step="basketStep"
      :is-price-section-sticky="isPriceSectionSticky"
      @order-checkout="orderCheckout"
      @go-to-payment="scrollToErrorBlock"
      @go-to-authorize="goToAuthorize"
      @go-to-address="scrollToErrorBlock"
      @go-to-delivery-at="scrollToErrorBlock"
      @go-to-discount="scrollToErrorBlock"
      @go-to-gifts="scrollToErrorBlock"
      @go-to-second-step="goToSecondStep"
    />
  </vee-field>
</template>

<style lang="scss" scoped>
.container {
  position: relative;
  display: grid;
  grid-template-rows: 1fr;
  gap: 8px 0;
  margin-bottom: 20px;
  overflow: hidden;

  &__line {
    margin: 8px 0;
    border: 0.5px solid var(--ui-grey);
  }

  &__total {
    font-size: 18px;
    font-weight: 800;
  }
}
</style>
